import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Icon } from '../../shared/ui/Icon'
import { colors } from '../../shared/ui/colors'
import { ShortObjectDto } from '../../api/documentation/types/model'
import {
  SearchContainer,
  ProjectInfo,
  ProjectPrefix,
  SelectedValueContainer,
  SelectedValueButton,
  SelectedValueText,
  SearchDropdown,
  SearchInputWrapper,
  SearchInput,
  SearchResultsList,
  SearchResultItem,
  SearchResultText,
  SearchResultCheck
} from './ProjectSelectorWithSearch.styles'

interface ProjectSelectorWithSearchProps {
  selectedObject: ShortObjectDto | null
  onObjectChange: (object: ShortObjectDto) => void
  objects: ShortObjectDto[]
  placeholder?: string
  searchPlaceholder?: string
}

export const ProjectSelectorWithSearch: React.FC<
  ProjectSelectorWithSearchProps
> = ({
  selectedObject,
  onObjectChange,
  objects,
  placeholder = 'Выберите проект',
  searchPlaceholder = 'Поиск ОИПД...'
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filteredObjects = useMemo(() => {
    if (!searchQuery.trim()) {
      return objects
    }
    const query = searchQuery.toLowerCase()
    return objects.filter(obj => obj.name.toLowerCase().includes(query))
  }, [objects, searchQuery])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const handleToggle = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setSearchQuery('')
    }
  }

  const handleSelect = (object: ShortObjectDto) => {
    onObjectChange(object)
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  return (
    <SearchContainer ref={containerRef}>
      <ProjectInfo>
        <Icon type="folder" size={16} color={colors.orange500} />
        <ProjectPrefix>ОИДП:</ProjectPrefix>
        <SelectedValueContainer>
          <SelectedValueButton onClick={handleToggle} type="button">
            <SelectedValueText>
              {selectedObject ? selectedObject.name : placeholder}
            </SelectedValueText>
            <Icon
              type={isOpen ? 'chevron_up' : 'chevron_down'}
              size={12}
              color={colors.gray600}
            />
          </SelectedValueButton>
        </SelectedValueContainer>
      </ProjectInfo>

      {isOpen && (
        <SearchDropdown>
          <SearchInputWrapper>
            <Icon type="search" size={16} color={colors.gray500} />
            <SearchInput
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={searchPlaceholder}
            />
          </SearchInputWrapper>

          <SearchResultsList>
            {filteredObjects.length === 0 ? (
              <SearchResultItem>
                <SearchResultText>Ничего не найдено</SearchResultText>
              </SearchResultItem>
            ) : (
              filteredObjects.map(object => {
                const isSelected = selectedObject?.id === object.id
                return (
                  <SearchResultItem
                    key={object.id}
                    onClick={() => handleSelect(object)}
                    isSelected={isSelected}
                  >
                    <SearchResultText>{object.name}</SearchResultText>
                    {isSelected && (
                      <SearchResultCheck>
                        <Icon type="check" size={14} color={colors.orange500} />
                      </SearchResultCheck>
                    )}
                  </SearchResultItem>
                )
              })
            )}
          </SearchResultsList>
        </SearchDropdown>
      )}
    </SearchContainer>
  )
}
